/* eslint-disable arrow-body-style */

import {
  APP_ROUTES,
  IDLE_TIMEOUT,
  LOCAL_STORAGE_KEYS,
  type ProviderSelectionSourcePath,
} from "@src/constants";
import type { ClinicianType, SpecialityMatchType } from "@src/constants/filter-constants";
import type {
  Provider,
  ProviderDepartment,
  ProviderSlot,
} from "@src/interfaces/provider.interface";
import type { TimezoneInfo } from "@src/interfaces/timezone.interface";
import type { UserRole } from "@src/interfaces/user-role.interface";
import ClientLogsService from "@src/services/client-logs.service";
import { canRoleBypassGuardrails } from "@src/utils/role-guardrails";
import type { FC, ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export interface SharedData {
  salesforceLeadId?: string;
  userUuid?: string;
  zip?: string;
  scheduleFor?: string;
  ageRange?: string;
  paymentMethod?: string;
  selectedPolicy?: string | null;
  canvasPrefillVersion?: number;
  insurancePolicies?: IdValue[];
  insurancePolicy?: IdValue | null;
  specialities?: string[];
  specialityMatchType?: SpecialityMatchType;
  // modalities?: string[];
  languages?: string;
  providerGender?: string;
  sessionType?: string | string[];
  careFormat?: string;
  dayOfWeek?: string[];
  startHour?: number;
  endHour?: number;
  requiresGetStartedServiceType?: boolean;
  // daysOfWeek?: string[];
  providerRadius?: string;
  typeOfService?: string;
  providerType?: ClinicianType;
  memberId?: string;
  dob?: string | null;
  gender?: string;
  isPrimaryPolicyHolder?: boolean | undefined;
  policyholderFirstName?: string;
  policyholderLastName?: string;
  relationshipToPolicyholder?: string;
  policyholderGender?: string;
  policyholderDOB?: string;
  coverCareForm?: FormDetails;
  policyholderForm?: FormDetails;
  yourInfoForm?: FormDetails;
  reviewDetailsForm?: FormDetails;
  selectedProvider?: Provider;
  isGetStartedChecked?: boolean | undefined;
  memberDetails?: MemberDetails;
  guardianDetails?: GuardianDetails;
  isGuardianPolicyHolder?: boolean;
  guardianSameAsPatientAddress?: boolean;

  //appointment date & session selection
  selectedDate?: string;
  selectedDepartment?: ProviderDepartment;
  selectedTimeSlot?: ProviderSlot;

  // Checkbox states for FormFooter
  termsChecked?: boolean;
  communicationChecked?: boolean;
  footerTermsCheckboxError?: boolean;
  footerCommunicationCheckboxError?: boolean;
  appointmentDetails?: AppointmentDetails;
  address?: Address;
  providerList?: Provider[];
  userTimezone?: TimezoneInfo;

  // Referrals
  selectedPartner?: string;
  selectedReferrerTeam?: string;
  selectedPartnerFilteredInsurancePackages?: Record<string, number[]>;
  hasPostedDigitalInfo?: boolean;

  // Set once address-search succeeds, confirming the backend users row exists.
  // Gates the user-lead PATCH so it never races ahead of user creation.
  userRecordCreated?: boolean;

  // Redirect flow flags
  isReferrerFlow?: boolean; // Referrer flow flag
  isReturningFromContactFlow?: boolean; // Contact flow flag to indicate if user has been on Contact Us/Contact page, used to trigger specific logic on Get Started page for users returning from Contact Us/Contact page

  // User role derived from session context
  userRole?: UserRole;

  // Records identity initialization completed before the feature manifest gate.
  entryRouteIdentityPath?: string;

  // True only after the API-assigned browse-first treatment resolves a ZIP.
  // This is UI state, never a backend authorization signal.
  isCombinedFindProviderTreatment?: boolean;

  // Salesforce test-lead indicator. When true the lead is a
  // test record: scheduling (freeze/book) is blocked and the API marks the
  // user_actions row as a test record.
  testRecord?: boolean;

  // Set when an under-12 user clicks "Proceed" on the in-person visit modal.
  // Used to lock session type to In-person in provider search filters.
  isUnder12InPersonProceed?: boolean;

  // Idle timer: suppress modal on /get-started until user has interacted with the form
  hasGetStartedFormInput?: boolean;

  // Idle timer: suppress modal on /refer until user has made a selection
  hasReferFormInput?: boolean;

  // Tracks whether the refer form is actively loading (partners or referrer teams)
  isReferFormLoading?: boolean;

  // Refer route initialization guard to prevent clear-on-remount during runtime theme changes
  hasReferInitialized?: boolean;

  // Navigation state flag to persist "came from screener" across route transitions
  fromScreener?: boolean;

  // Tracks where the currently selected provider was chosen so /provider-profile
  // can send Back to the correct origin.
  providerSelectionSource?: ProviderSelectionSourcePath;

  // Meet the Team only: whether the selected provider was in the latest provider search
  // results. Set by Get Started and read by the screener to choose its next route.
  isMeetTheTeamProviderAvailable?: boolean;

  // Tracks which provider-selection route the user navigated from before entering
  // patient-information, so the Back button returns them to the correct page.
  // Set to "/find-provider" or "/provider-profile" at the point of navigation.
  providerBackPath?: string;

  // Patient screener answers
  patientScreenerAnswers?: PatientScreenerAnswers;

  // Med management indicator: false by default, set to true on PatientScreenerForm load
  medMgmtInd?: boolean;

  // Qualtrics survey tracking
  lastpageURL?: string;
  completionStatus?: boolean;
}

export type { UserRole } from "@src/interfaces/user-role.interface";

export interface PatientScreenerAnswers {
  hospitalized?: boolean;
  eatingDisorder?: boolean;
  seriousMentalIllness?: boolean;
  longActingInjectable?: boolean;
  opioidTreatment?: boolean;
  controlledSubstances?: boolean;
}

export interface MemberDetails {
  firstName?: string;
  lastName?: string;
  genderIdentity?: string;
  preferredPronouns?: string;
  language?: string;
  streetAddress1?: string;
  streetAddress2?: string;
  zip?: string;
  city?: string;
  state_code?: string;
  phoneType?: string;
  preferredContactMethod?: string;
  voicemailPreference?: string;
  voicemailConsent?: string;
  smsConsent?: string;
  legalSex?: string;
  email?: string;
  phone?: string;
}

export interface GuardianDetails {
  firstName?: string;
  lastName?: string;
  dob?: string;
  legalSex?: string;
  genderIdentity?: string;
  preferredPronouns?: string;
  preferredLanguage?: string;
  streetAddress1?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  email?: string;
  phone?: string;
  phoneType?: string;
  preferredContactMethod?: string;
  voicemailPreference?: string;
}
export interface Address {
  city: string;
  state: string;
  position: {
    lat: number;
    lon: number;
  };
  state_code: string;
  context_id: number;
}

export interface IdValue {
  id: string;
  name: string;
  payer_id?: number | string;
}

export interface AppointmentDetails {
  providerId?: string;
  apptLocation?: ProviderDepartment;
  apptDate?: string;
  apptTime?: string;
  careFormat?: string;
  appointmentIds?: number[]; // Array of appointment slot IDs
  appointmentTypeId?: number; // Appointment type ID
  userTimezone?: string;
}

export interface FormDetails {
  isCompleted: boolean;
  goToNextSubStep?: boolean;
  isUnder18?: boolean;
  memberDOB?: string | Date | undefined;
  /** Snapshot of SharedData after form values have been applied.
   *  Used by NavButtons to build the Canvas outbound payload without
   *  waiting for React state to flush. */
  submittedData?: Partial<SharedData>;
}

// Define the shape of the context data
interface ApplicationContextType {
  data: Partial<SharedData>;
  setData: (key: keyof SharedData, value: any) => void;
  setDataPatch: (patch: Partial<SharedData>) => void;
  removeValueFromKey: (key: keyof SharedData, value: any) => void;
  clearData: () => void;
  clearDataExcluding: (fieldsToKeep: (keyof SharedData)[]) => void;
  validateFooterCheckboxes: () => boolean;
  clearAdditionalSavedModalData: () => void;
  /** Convenience: calls clearData + clearAdditionalSavedModalData + clearCompletedRoutes in one shot. */
  clearSession: () => void;
  /** Clears appointment booking state; call instead of manually nulling appointmentDetails and selectedTimeSlot. */
  clearAppointmentSelection: () => void;
  /** Removes all lead-scoped localStorage keys for the confirmed lead; no-op when no leadId is set. */
  clearLeadScopedStorage: () => void;
  completedRoutes: Record<string, boolean>;
  markRouteAsComplete: (route: string) => void;
  clearCompletedRoutes: () => void;
}

// Create the context
const ApplicationContext = createContext<ApplicationContextType | undefined>(undefined);

const DEFAULT_COMPLETED_ROUTES: Record<string, boolean> = {
  [APP_ROUTES.REFER]: false,
  [APP_ROUTES.GET_STARTED]: false,
  [APP_ROUTES.MEET_THE_TEAM]: false,
  [APP_ROUTES.FIND_PROVIDER]: false,
  [APP_ROUTES.PROVIDER_PROFILE]: false,
  [APP_ROUTES.PATIENT_INFORMATION]: false,
};

const getLeadScopedKey = (baseKey: string, leadId?: string): string => {
  const trimmedLeadId = leadId?.trim();
  return trimmedLeadId ? `${baseKey}:${trimmedLeadId}` : baseKey;
};

const LEAD_SCOPED_BASE_KEYS = [
  "sessionData",
  "completedRoutes",
  "lastActivityAt",
  "sessionExpirySeconds",
] as const;

/**
 * Evicts lead-scoped localStorage entries for leads other than `currentLeadId`
 * that are expired or have no activity timestamp (truly orphaned).
 * Fresh leads are kept so multi-tab and lead-switching-back scenarios are unaffected.
 */
const purgeOrphanedLeadStorage = (currentLeadId: string): void => {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      for (const base of LEAD_SCOPED_BASE_KEYS) {
        if (key.startsWith(`${base}:`)) {
          const storedLeadId = key.slice(base.length + 1);
          if (storedLeadId !== currentLeadId) {
            const lastActivity = localStorage.getItem(`lastActivityAt:${storedLeadId}`);
            const storedExpiry = Number(
              localStorage.getItem(`sessionExpirySeconds:${storedLeadId}`),
            );
            const expiryMs = (storedExpiry > 0 ? storedExpiry : IDLE_TIMEOUT) * 1000;
            // No lastActivityAt means the lead was never active or is truly orphaned.
            const isStale = lastActivity ? Date.now() - Number(lastActivity) > expiryMs : true;
            if (isStale) keysToRemove.push(key);
          }
          break;
        }
      }
    }
    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });
  } catch {
    // localStorage unavailable; ignore
  }
};

// Fields sourced from the Salesforce Canvas prefill event (see
// services/canvas/prefill/event-prefill-mapper.ts). When a Patient Navigator
// session expires we preserve these so the navigator does not have to re-enter
// data Salesforce already provided, while still dropping in-progress selections.
const CANVAS_PREFILL_PRESERVED_KEYS: (keyof SharedData)[] = [
  "userRole",
  "canvasPrefillVersion",
  "memberDetails",
  "zip",
  "dob",
  "ageRange",
  "gender",
  "testRecord",
  "paymentMethod",
  "memberId",
  "policyholderFirstName",
  "policyholderLastName",
  "policyholderDOB",
  "policyholderGender",
  "relationshipToPolicyholder",
  "isPrimaryPolicyHolder",
  "insurancePolicy",
  "selectedPolicy",
];

const pickCanvasPrefillData = (source: Partial<SharedData>): Partial<SharedData> => {
  const preserved: Partial<SharedData> = {};
  CANVAS_PREFILL_PRESERVED_KEYS.forEach((key) => {
    if (source[key] !== undefined) {
      (preserved as Record<string, unknown>)[key] = source[key];
    }
  });
  return preserved;
};

const readStoredSessionData = (leadId?: string): Partial<SharedData> => {
  const storedData = localStorage.getItem(getLeadScopedKey("sessionData", leadId));

  if (!storedData) {
    return {};
  }

  try {
    const parsed = JSON.parse(storedData) as Partial<SharedData>;

    // If the user closed the browser while idle, the in-app timer never fired.
    // Compare lastActivityAt against the effective session expiry that was written
    // by IdleTimerContext on each activity event. Fall back to compile-time defaults
    // if the key is absent (e.g. first load after deploy, or private browsing).
    try {
      const lastActivity = localStorage.getItem(getLeadScopedKey("lastActivityAt", leadId));
      if (lastActivity) {
        const elapsed = Date.now() - Number(lastActivity);
        const storedExpiry = Number(
          localStorage.getItem(getLeadScopedKey("sessionExpirySeconds", leadId)),
        );
        const expiryMs = (storedExpiry > 0 ? storedExpiry : IDLE_TIMEOUT) * 1000;

        if (elapsed > expiryMs) {
          localStorage.removeItem(getLeadScopedKey("completedRoutes", leadId));
          localStorage.removeItem(getLeadScopedKey("lastActivityAt", leadId));
          localStorage.removeItem(getLeadScopedKey("sessionExpirySeconds", leadId));
          // The idle modal may have been showing (warning/confirmation/timeout) when
          // the browser was closed. Clear its residual phase and any modal-read flags
          // so the next session starts clean.
          localStorage.removeItem("idlePhase");

          // Patient Navigators work inside the Salesforce Canvas iframe where the
          // patient identity/insurance data is supplied by Salesforce, not entered
          // by the navigator. Preserve those Canvas-prefilled fields on expiry so the
          // navigator does not have to re-key them; only in-progress selections are
          // dropped. All other roles start empty.
          if (parsed.userRole === "patient navigator") {
            const preserved = pickCanvasPrefillData(parsed);
            localStorage.setItem(
              getLeadScopedKey("sessionData", leadId),
              JSON.stringify(preserved),
            );
            return preserved;
          }

          localStorage.removeItem(getLeadScopedKey("sessionData", leadId));
          return {};
        }
      }
    } catch {
      // If localStorage access fails, preserve the parsed session instead of clearing it.
    }

    return parsed;
  } catch {
    return {};
  }
};

const readStoredCompletedRoutes = (leadId?: string): Record<string, boolean> => {
  const storedRoutes = localStorage.getItem(getLeadScopedKey("completedRoutes", leadId));

  if (!storedRoutes) {
    return { ...DEFAULT_COMPLETED_ROUTES };
  }

  try {
    return { ...DEFAULT_COMPLETED_ROUTES, ...JSON.parse(storedRoutes) };
  } catch {
    return { ...DEFAULT_COMPLETED_ROUTES };
  }
};

// Provider component
export const ApplicationProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [data, setDataState] = useState<Partial<SharedData>>(() => {
    return readStoredSessionData();
  });

  // Initialize completedRoutes from localStorage or default to all routes as incomplete
  const [completedRoutes, setCompletedRoutes] = useState<Record<string, boolean>>(() => {
    return readStoredCompletedRoutes();
  });
  const [hydratedLeadId, setHydratedLeadId] = useState<string | undefined>(() => {
    return data.salesforceLeadId?.trim();
  });

  const activeLeadId = data.salesforceLeadId?.trim();

  useEffect(() => {
    if (!activeLeadId) {
      setHydratedLeadId(undefined);
      return;
    }

    if (hydratedLeadId === activeLeadId) {
      return;
    }

    purgeOrphanedLeadStorage(activeLeadId);

    setDataState((prevData) => {
      const storedData = readStoredSessionData(activeLeadId);
      return {
        ...storedData,
        ...prevData,
        salesforceLeadId: activeLeadId,
      };
    });

    setCompletedRoutes(readStoredCompletedRoutes(activeLeadId));
    setHydratedLeadId(activeLeadId);
  }, [activeLeadId, hydratedLeadId]);

  // Update localStorage whenever data changes
  useEffect(() => {
    if (activeLeadId && hydratedLeadId !== activeLeadId) {
      return;
    }

    const key = getLeadScopedKey("sessionData", activeLeadId);
    const value = JSON.stringify(data);
    try {
      localStorage.setItem(key, value);
    } catch {
      // Quota exceeded: evict orphaned leads and retry once to prevent a crash.
      if (activeLeadId) purgeOrphanedLeadStorage(activeLeadId);
      try {
        localStorage.setItem(key, value);
      } catch {
        // Still no space — degrade gracefully; in-memory state is still intact.
      }
    }
  }, [data, activeLeadId, hydratedLeadId]);

  // Keep client logs enriched with the active user role as soon as it is known.
  useEffect(() => {
    ClientLogsService.setUserRole(data.userRole);
  }, [data.userRole]);

  // Function to update a specific key in the local data
  const setData = useCallback((key: keyof SharedData, value: any) => {
    setDataState((prevData) => {
      if (Object.is(prevData[key], value)) {
        return prevData;
      }

      return { ...prevData, [key]: value };
    });
  }, []);

  const setDataPatch = useCallback((patch: Partial<SharedData>) => {
    setDataState((prevData) => ({ ...prevData, ...patch }));
  }, []);

  // Remove specific value from a key if it matches (for array values)
  const removeValueFromKey = (key: keyof SharedData, value: any): void => {
    setDataState((prevData: Partial<SharedData>): Partial<SharedData> => {
      const currentValue = prevData[key];

      // If the current value is an array, remove the matching value
      if (Array.isArray(currentValue)) {
        const filteredArray = currentValue.filter((item) => item !== value);

        return {
          ...prevData,
          [key]: filteredArray,
        };
      }

      // If the current value matches the value to remove (for string values)
      if (currentValue === value) {
        return {
          ...prevData,
          [key]: "",
        };
      }

      return prevData;
    });
  };

  // Function to clear all local data
  const clearData = useCallback(() => {
    setDataState({});
    localStorage.removeItem(getLeadScopedKey("sessionData", activeLeadId));
    localStorage.removeItem(getLeadScopedKey("lastActivityAt", activeLeadId));
    localStorage.removeItem(getLeadScopedKey("sessionExpirySeconds", activeLeadId));
  }, [activeLeadId]);

  // Function to clear all fields except specified exclusions
  const clearDataExcluding = useCallback((fieldsToKeep: (keyof SharedData)[]): void => {
    setDataState((prevData: Partial<SharedData>): Partial<SharedData> => {
      const clearedData: Partial<SharedData> = {};

      // Only keep the fields that are in the exclusion array
      fieldsToKeep.forEach((field) => {
        if (field in prevData) {
          (clearedData as any)[field] = prevData[field];
        }
      });

      return clearedData;
    });
    // The useEffect will automatically sync the cleared data to localStorage
  }, []);

  // Clear specific saved modal data in localStorage to prevent stale modal state issues on future modals if user navigates back to home page after interacting with a modal
  const clearAdditionalSavedModalData = useCallback(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.FIND_MEMBER_ID_MODAL);
  }, []);

  // Function to validate footer checkboxes
  const validateFooterCheckboxes = () => {
    if (canRoleBypassGuardrails(data.userRole)) {
      setDataState((prevData) => ({
        ...prevData,
        footerTermsCheckboxError: false,
        footerCommunicationCheckboxError: false,
      }));
      return true;
    }

    const errors = {
      footerTermsCheckboxError: !data.termsChecked,
      footerCommunicationCheckboxError: !data.communicationChecked,
    };
    setDataState((prevData) => ({ ...prevData, ...errors }));
    return !errors.footerTermsCheckboxError && !errors.footerCommunicationCheckboxError;
  };

  // Save completedRoutes to localStorage whenever it changes
  useEffect(() => {
    if (activeLeadId && hydratedLeadId !== activeLeadId) {
      return;
    }

    const key = getLeadScopedKey("completedRoutes", activeLeadId);
    const value = JSON.stringify(completedRoutes);
    try {
      localStorage.setItem(key, value);
    } catch {
      // Quota exceeded: evict orphaned leads and retry once to prevent a crash.
      if (activeLeadId) purgeOrphanedLeadStorage(activeLeadId);
      try {
        localStorage.setItem(key, value);
      } catch {
        // Still no space — degrade gracefully; in-memory state is still intact.
      }
    }
  }, [completedRoutes, activeLeadId, hydratedLeadId]);

  // Mark a route as complete
  const markRouteAsComplete = useCallback((route: string) => {
    setCompletedRoutes((prev) => ({ ...prev, [route]: true }));
  }, []);

  const clearCompletedRoutes = useCallback(() => {
    setCompletedRoutes({ ...DEFAULT_COMPLETED_ROUTES });
    localStorage.removeItem(getLeadScopedKey("completedRoutes", activeLeadId));
  }, [activeLeadId]);

  const clearAppointmentSelection = useCallback(() => {
    setDataState((prevData) => ({
      ...prevData,
      appointmentDetails: undefined,
      selectedTimeSlot: undefined,
    }));
  }, []);

  const clearLeadScopedStorage = useCallback(() => {
    if (!activeLeadId) return;
    localStorage.removeItem(getLeadScopedKey("sessionData", activeLeadId));
    localStorage.removeItem(getLeadScopedKey("completedRoutes", activeLeadId));
    localStorage.removeItem(getLeadScopedKey("lastActivityAt", activeLeadId));
    localStorage.removeItem(getLeadScopedKey("sessionExpirySeconds", activeLeadId));
  }, [activeLeadId]);

  const clearSession = useCallback(() => {
    // Patient Navigators work inside the Salesforce Canvas iframe where patient
    // identity/insurance data is supplied by Salesforce, not entered by the
    // navigator. When a session is cleared (idle end/timeout), preserve those
    // Canvas-prefilled fields so the navigator does not have to re-key them;
    // only in-progress selections are dropped. All other roles clear fully.
    if (data.userRole === "patient navigator") {
      clearDataExcluding(CANVAS_PREFILL_PRESERVED_KEYS);
      // Dropping salesforceLeadId changes lead scoping, so the old lead-scoped
      // sessionData snapshot would otherwise be orphaned with stale in-progress
      // data (e.g. userUuid). Remove it explicitly; the preserved subset is
      // re-persisted by the sync effect under the (now unscoped) key.
      localStorage.removeItem(getLeadScopedKey("sessionData", activeLeadId));
      localStorage.removeItem(getLeadScopedKey("lastActivityAt", activeLeadId));
      localStorage.removeItem(getLeadScopedKey("sessionExpirySeconds", activeLeadId));
    } else {
      clearData();
    }
    clearAdditionalSavedModalData();
    clearCompletedRoutes();
  }, [
    data.userRole,
    activeLeadId,
    clearData,
    clearDataExcluding,
    clearAdditionalSavedModalData,
    clearCompletedRoutes,
  ]);

  const contextValue = useMemo(
    () => ({
      data,
      setData,
      setDataPatch,
      removeValueFromKey,
      clearData,
      clearDataExcluding,
      validateFooterCheckboxes,
      clearAdditionalSavedModalData,
      clearSession,
      clearAppointmentSelection,
      clearLeadScopedStorage,
      completedRoutes,
      markRouteAsComplete,
      clearCompletedRoutes,
    }),
    [
      data,
      setData,
      setDataPatch,
      removeValueFromKey,
      clearData,
      clearDataExcluding,
      validateFooterCheckboxes,
      clearAdditionalSavedModalData,
      clearSession,
      clearAppointmentSelection,
      clearLeadScopedStorage,
      completedRoutes,
      markRouteAsComplete,
      clearCompletedRoutes,
    ],
  );

  return <ApplicationContext.Provider value={contextValue}>{children}</ApplicationContext.Provider>;
};

// Hook to use the application context
export const useApplicationContext = () => {
  const context = useContext(ApplicationContext);
  if (!context) {
    throw new Error("useApplicationContext must be used within a ApplicationProvider");
  }
  return context;
};

// Optional hook for shared components that can render inside or outside ApplicationProvider.
export const useOptionalApplicationContext = () => {
  return useContext(ApplicationContext);
};
